'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AnnouncementBar from '@/components/AnnouncementBar';
import { getProductById, getRelatedProducts, ProductData } from '@/lib/productData';

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}

function LeafIcon() {
  return (
    <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3C7 3 3 7 3 12c0 4 2.5 7.5 6 9 .5-3 2-5.5 4-7-2 3-2.5 5.5-2 8 .5.5 1 1 1 1s.5-.5 1-1c.5-2.5 0-5-2-8 2 1.5 3.5 4 4 7 3.5-1.5 6-5 6-9 0-5-4-9-9-9z" />
    </svg>
  );
}

export default function ProductDetailPage() {
  const params = useParams();
  const id = params?.id as string;
  const [product, setProduct] = useState<ProductData | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<ProductData[]>([]);
  const [activeTab, setActiveTab] = useState<'specs' | 'materials' | 'customization'>('specs');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (id) {
      const found = getProductById(id);
      if (found) {
        setProduct(found);
        setRelatedProducts(getRelatedProducts(found.relatedIds || []));
      }
    }
    const t = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(t);
  }, [id]);

  if (!product) {
    return (
      <main className="min-h-screen bg-white">
        <AnnouncementBar />
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-32 text-center">
          <h1 className="text-3xl font-bold text-primary-dark mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            Product not found
          </h1>
          <p className="text-gray-500 mb-8">The product you're looking for doesn't exist or has been removed.</p>
          <Link href="/" className="btn-primary">← Back to Home</Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white">
      <AnnouncementBar />
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link href="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <Link href="/#products" className="hover:text-primary transition-colors">Products</Link>
            <span>/</span>
            <span className="text-primary-dark font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className={`py-10 lg:py-16 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* Left: Product Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden bg-gray-50 aspect-square lg:aspect-[4/3]">
                <img
                  src={product.image}
                  alt={product.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.badge && (
                <span className="absolute top-4 left-4 bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                  {product.badge}
                </span>
              )}
              {/* Eco badge */}
              <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2 flex items-center gap-2 shadow-md border border-primary/10">
                <LeafIcon />
                <span className="text-xs font-semibold text-primary-dark">Eco-Conscious</span>
              </div>
            </div>

            {/* Right: Product Info */}
            <div className="flex flex-col gap-5">
              <div>
                <span className="inline-block text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full uppercase tracking-wider mb-3">
                  {product.category}
                </span>
                <h1
                  className="text-3xl md:text-4xl lg:text-[2.6rem] font-bold text-primary-dark leading-tight mb-3"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  {product.name}
                </h1>
                <p className="text-base text-gray-500 font-medium italic mb-4">{product.tagline}</p>
                <p className="text-gray-600 text-base leading-relaxed">{product.description}</p>
              </div>

              {/* Key Highlights */}
              <div className="grid grid-cols-2 gap-2">
                {product.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2 bg-primary/5 rounded-lg px-3 py-2">
                    <CheckIcon />
                    <span className="text-xs font-medium text-primary-dark">{h}</span>
                  </div>
                ))}
              </div>

              {/* MOQ & Lead Time */}
              <div className="flex flex-wrap gap-4 py-4 border-t border-b border-gray-100">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-0.5">Min. Order Qty</p>
                  <p className="text-base font-bold text-primary-dark">{product.moq}</p>
                </div>
                <div className="w-px bg-gray-200" />
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-0.5">Lead Time</p>
                  <p className="text-base font-bold text-primary-dark">{product.leadTime}</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="#get-quote"
                  className="btn-primary flex-1 text-center justify-center"
                >
                  Get a Custom Quote
                </a>
                <a
                  href="#get-quote"
                  className="btn-outline flex-1 text-center justify-center"
                >
                  Request a Sample
                </a>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Free design assistance
                </span>
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Sample kit available
                </span>
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Zero virgin plastic
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabbed Details Section */}
      <section className="py-10 bg-gray-50/60 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Tab Nav */}
          <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-100 shadow-sm w-fit mb-8">
            {(['specs', 'materials', 'customization'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 capitalize ${
                  activeTab === tab
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-gray-500 hover:text-primary-dark'
                }`}
              >
                {tab === 'specs' ? 'Specifications' : tab === 'materials' ? 'Materials' : 'Customization'}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2">
              {activeTab === 'specs' && (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-100 bg-primary/5">
                    <h2 className="text-lg font-bold text-primary-dark" style={{ fontFamily: 'Fraunces, serif' }}>
                      Product Specifications
                    </h2>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {product.specs.map((spec, i) => (
                      <div key={i} className="flex items-start px-6 py-4 gap-4">
                        <span className="text-sm font-semibold text-gray-500 w-40 flex-shrink-0">{spec.label}</span>
                        <span className="text-sm text-primary-dark font-medium">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'materials' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-100 bg-primary/5">
                      <h2 className="text-lg font-bold text-primary-dark" style={{ fontFamily: 'Fraunces, serif' }}>
                        Materials Used
                      </h2>
                    </div>
                    <div className="p-6 space-y-3">
                      {product.materials.map((mat, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <LeafIcon />
                          </div>
                          <span className="text-sm text-gray-700 font-medium">{mat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {product.materialNote && (
                    <div className="bg-primary/8 border border-primary/20 rounded-xl px-5 py-4 flex items-start gap-3">
                      <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-primary-dark font-medium">{product.materialNote}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'customization' && (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-100 bg-primary/5">
                    <h2 className="text-lg font-bold text-primary-dark" style={{ fontFamily: 'Fraunces, serif' }}>
                      Customization Options
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {product.customizationOptions.map((opt, i) => (
                        <div key={i} className="flex items-start gap-2.5 bg-gray-50 rounded-lg px-4 py-3">
                          <CheckIcon />
                          <span className="text-sm text-gray-700 font-medium">{opt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar: Use Cases */}
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 bg-primary/5">
                  <h3 className="text-base font-bold text-primary-dark" style={{ fontFamily: 'Fraunces, serif' }}>
                    Best For
                  </h3>
                </div>
                <div className="p-5 space-y-2">
                  {product.useCases.map((uc, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span className="text-sm text-gray-600">{uc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick CTA card */}
              <div className="bg-primary-dark rounded-2xl p-5 text-white">
                <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  Ready to order?
                </h3>
                <p className="text-white/70 text-xs mb-4 leading-relaxed">
                  Share your requirements and get a quote within 24 hours.
                </p>
                <a href="#get-quote" className="block w-full text-center bg-white text-primary-dark font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                  Get a Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote / Contact CTA Section */}
      <section id="get-quote" className="py-14 lg:py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Info */}
            <div>
              <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4">
                Get Started
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold text-primary-dark mb-4"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Get a custom quote for {product.name}
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                Tell us your quantity, size, and branding requirements. Our team will get back to you within 24 hours with a detailed quote and timeline.
              </p>
              <div className="space-y-3">
                {[
                  'Free design consultation included',
                  'Physical sample available before bulk order',
                  `MOQ starts at ${product.moq}`,
                  `Standard lead time: ${product.leadTime}`,
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckIcon />
                    <span className="text-sm text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-gray-50 rounded-2xl p-6 lg:p-8 border border-gray-100">
              <h3 className="text-lg font-bold text-primary-dark mb-5" style={{ fontFamily: 'Fraunces, serif' }}>
                Request a Quote
              </h3>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Your Name</label>
                    <input
                      type="text"
                      placeholder="Priya Sharma"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Company</label>
                    <input
                      type="text"
                      placeholder="Brand Co."
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email</label>
                  <input
                    type="email"
                    placeholder="hello@yourbrand.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 bg-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Product</label>
                    <input
                      type="text"
                      value={product.name}
                      readOnly
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-500 bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Quantity</label>
                    <input
                      type="text"
                      placeholder={`Min. ${product.moq}`}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Requirements & Notes</label>
                  <textarea
                    rows={3}
                    placeholder="Describe your size, print, and customization needs..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 bg-white resize-none"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full justify-center"
                >
                  Send Quote Request
                </button>
                <p className="text-center text-xs text-gray-400">We respond within 24 hours · No spam, ever.</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-12 bg-gray-50/60 border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2
              className="text-2xl md:text-3xl font-bold text-primary-dark mb-8"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              You might also like
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {relatedProducts.map((rel) => (
                <Link
                  key={rel.id}
                  href={`/products/${rel.id}`}
                  className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-card hover:-translate-y-1 transition-all duration-200"
                >
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    <img
                      src={rel.image}
                      alt={rel.alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3">
                    <span className="text-xs text-gray-400 font-medium">{rel.category}</span>
                    <h3 className="text-sm font-semibold text-primary-dark mt-0.5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {rel.name}
                    </h3>
                    <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover-arrow">
                      View details
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}
